import { observer } from 'mobx-react-lite'
import { TextFieldWithLabel, Typography } from 'shared/ui'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { WhereCanIFindBusinessInfoButton } from 'pages/settings/ui/LinkButton/WhereCanIFindBusinessInfoButton'
import { useRcsWizardContext } from 'pages/settings/pages/rcsV2/context/rcsWizardContext'
import {
  RCS_SUPPORTED_COUNTRIES,
  US_COUNTRY_ISO_CODE,
} from 'pages/settings/pages/rcsV2/lib/constants'

import styles from '../styles.module.scss'

const COUNTRY_ITEMS = RCS_SUPPORTED_COUNTRIES.filter(
  (country) => country.isoCode === US_COUNTRY_ISO_CODE
).map((country) => ({ id: country.isoCode, label: country.label }))

type IAddressFieldsProps = {
  disabled?: boolean
}

export const AddressFields = observer(({ disabled = false }: IAddressFieldsProps) => {
  const {
    businessInfoStore: {
      einError,
      einDisplayValue,
      einPlaceholder,
      setEin,
      validateEin,
      businessName,
      businessNameError,
      businessNameMaxLength,
      setBusinessName,
      validateBusinessName,
      addressCountry,
      streetAddress1,
      streetAddress1Error,
      streetAddress1MaxLength,
      setStreetAddress1,
      validateStreetAddress1,
      streetAddress2,
      streetAddress2MaxLength,
      setStreetAddress2,
      city,
      cityError,
      cityMaxLength,
      setCity,
      validateCity,
      state,
      stateError,
      stateOptions,
      statesLoading,
      setState,
      loadStates,
      postalCode,
      postalCodeError,
      setPostalCode,
      validatePostalCode,
    },
  } = useRcsWizardContext()

  return (
    <>
      {!disabled && (
        <TextFieldWithLabel
          marginBottom={0}
          errorBorder={Boolean(einError)}
          errorMessage={einError}
          labelProps={{
            label: {
              text: 'Employer Identification Number (EIN)',
              rightNode: <WhereCanIFindBusinessInfoButton />,
            },
          }}
          textFieldProps={{
            size: 'medium',
            value: einDisplayValue,
            onChange: setEin,
            onBlur: validateEin,
            InputProps: { placeholder: einPlaceholder },
          }}
        />
      )}

      <TextFieldWithLabel
        marginBottom={0}
        limit={businessNameMaxLength}
        errorBorder={Boolean(businessNameError)}
        errorMessage={businessNameError}
        labelProps={{
          label: {
            text: 'Legal business name',
            rightNode: <WhereCanIFindBusinessInfoButton />,
          },
        }}
        textFieldProps={{
          size: 'medium',
          disabled,
          value: businessName,
          onChange: setBusinessName,
          onBlur: validateBusinessName,
          InputProps: { placeholder: 'e.g. Example LLC' },
        }}
        bottomLabelProps={{
          label: {
            text: (
              <Typography
                variant='text-input-helper'
                color='var(--content-primary-tertiary)'
                ariaLabel='BusinessInfo_legalNameHint'
              >
                Enter the exact legal business name, as registered with the EIN
              </Typography>
            ),
          },
        }}
      />

      <SelectField
        disabled
        size='medium'
        labelProps={{ label: { text: 'Country' } }}
        items={COUNTRY_ITEMS}
        value={addressCountry}
        setValue={() => {}}
      />

      <TextFieldWithLabel
        marginBottom={0}
        errorBorder={Boolean(streetAddress1Error)}
        errorMessage={streetAddress1Error}
        limit={streetAddress1MaxLength}
        labelProps={{ label: { text: 'Business address' } }}
        textFieldProps={{
          size: 'medium',
          disabled,
          value: streetAddress1,
          onChange: setStreetAddress1,
          onBlur: validateStreetAddress1,
          InputProps: { placeholder: 'Enter your address' },
        }}
        bottomLabelProps={{
          label: {
            text: (
              <Typography
                variant='text-input-helper'
                color='var(--content-primary-tertiary)'
                ariaLabel='BusinessInfo_addressHint'
              >
                Enter the exact business address, as registered with the EIN
              </Typography>
            ),
          },
        }}
      />

      <TextFieldWithLabel
        marginBottom={0}
        limit={streetAddress2MaxLength}
        labelProps={{ label: { text: 'Business address 2 (optional)' } }}
        textFieldProps={{
          size: 'medium',
          disabled,
          value: streetAddress2,
          onChange: setStreetAddress2,
          InputProps: { placeholder: 'e.g. Building name' },
        }}
      />

      <TextFieldWithLabel
        marginBottom={0}
        errorBorder={Boolean(cityError)}
        errorMessage={cityError}
        limit={cityMaxLength}
        labelProps={{ label: { text: 'City' } }}
        textFieldProps={{
          size: 'medium',
          disabled,
          value: city,
          onChange: setCity,
          onBlur: validateCity,
          InputProps: { placeholder: 'e.g. Los Angeles' },
        }}
      />

      <div className={styles.row}>
        <div className={styles.rowItem}>
          <SelectField
            size='medium'
            disabled={disabled}
            labelProps={{ label: { text: 'State' } }}
            placeholder='Select'
            items={stateOptions}
            loading={statesLoading}
            value={state}
            setValue={setState}
            error={stateError}
            dropdownProps={{ onToggle: (open) => open && loadStates() }}
          />
        </div>
        <div className={styles.rowItem}>
          <TextFieldWithLabel
            marginBottom={0}
            widthWrap='100%'
            errorBorder={Boolean(postalCodeError)}
            errorMessage={postalCodeError}
            labelProps={{ label: { text: 'ZIP code' } }}
            textFieldProps={{
              size: 'medium',
              disabled,
              value: postalCode,
              onChange: setPostalCode,
              onBlur: validatePostalCode,
              InputProps: { placeholder: 'e.g. 90011' },
            }}
          />
        </div>
      </div>
    </>
  )
})
